import { useState, useEffect, useCallback } from 'react';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Toaster as HotToaster } from 'react-hot-toast';
import {
  TooltipProvider,
  NetworkValidator,
  OrientationWarning,
} from '@/components/common';
import { router } from './routes';
import { NetworkErrorOverlay } from '@shared/components';
import { MusicProvider } from '@/contexts/MusicContext';
import { SoundEffectProvider } from '@/contexts/SoundEffectContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { SessionExpiredHandler } from '@/components/SessionExpiredHandler';
import {
  LeaderboardRewardModal,
  type LeaderboardRewardData,
} from '@/components/modals/LeaderboardRewardModal';
import {
  useLeaderboardRewards,
  useAcknowledgeReward,
} from '@shared/hooks/useLeaderboardRewards';
import type { PendingReward } from '@services';

// Convert API PendingReward to modal LeaderboardRewardData
const toModalReward = (reward: PendingReward): LeaderboardRewardData => ({
  rank: reward.rank,
  period: reward.period,
  leaderboardType: reward.leaderboardType,
  gold: reward.gold,
  energyBoost: reward.energyBoost,
  durationDays: reward.durationDays,
});

function App() {
  const { pendingRewards, hasPendingRewards } = useLeaderboardRewards();
  const { acknowledge } = useAcknowledgeReward();

  // Queue of rewards to show
  const [rewardQueue, setRewardQueue] = useState<PendingReward[]>([]);
  const [currentReward, setCurrentReward] = useState<PendingReward | null>(
    null
  );
  const [showModal, setShowModal] = useState(false);

  // Initialize queue when pending rewards load
  useEffect(() => {
    if (hasPendingRewards && rewardQueue.length === 0 && !currentReward) {
      // Small delay to let app render first
      const timer = setTimeout(() => {
        setRewardQueue([...pendingRewards]);
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [hasPendingRewards, pendingRewards, rewardQueue.length, currentReward]);

  // Show next reward from queue
  useEffect(() => {
    if (rewardQueue.length > 0 && !currentReward) {
      const [nextReward, ...rest] = rewardQueue;
      setCurrentReward(nextReward);
      setRewardQueue(rest);
      setShowModal(true);
    }
  }, [rewardQueue, currentReward]);

  // Handle modal close - acknowledge and show next
  const handleCloseModal = useCallback(async () => {
    if (currentReward) {
      try {
        await acknowledge(currentReward.id);
      } catch (error) {
        console.error('Failed to acknowledge reward:', error);
      }
    }
    setShowModal(false);
    // Small delay before showing next reward for better UX
    setTimeout(() => {
      setCurrentReward(null);
    }, 300);
  }, [currentReward, acknowledge]);

  return (
    <ErrorBoundary>
      <MusicProvider
        src="/music/background.mp3"
        volume={1}
        autoPlay={true}
        loop={true}
      >
        <SoundEffectProvider
          clickSoundUrl="/music/button-click.mp3"
          volume={0.5}
          enableGlobalClickSound={true}
        >
          <TooltipProvider delayDuration={0} skipDelayDuration={0}>
            <Toaster
              position="top-center"
              toastOptions={{
                style: {
                  fontFamily: "'Lilita One', sans-serif",
                  fontSize: '16px',
                  borderRadius: '12px',
                  border: '2px solid rgba(0, 0, 0, 0.7)',
                  boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.45)',
                  backdropFilter: 'blur(4px)',
                },
                className: 'game-toast',
              }}
              richColors
            />
            <HotToaster
              position="top-center"
              reverseOrder={false}
              gutter={8}
              containerStyle={{
                top: 80,
              }}
              toastOptions={{
                duration: 3000,
                style: {
                  fontFamily: "'Lilita One', sans-serif",
                  fontSize: '16px',
                  borderRadius: '12px',
                  border: '2px solid rgba(0, 0, 0, 0.7)',
                  boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.45)',
                },
              }}
              containerClassName="toast-container"
            />
            <div className="game-viewport">
              <div className="game-container">
                <RouterProvider router={router} />
                <NetworkErrorOverlay />
                <NetworkValidator />
                <OrientationWarning />
                <SessionExpiredHandler />
                <LeaderboardRewardModal
                  isOpen={showModal}
                  reward={currentReward ? toModalReward(currentReward) : null}
                  onClose={handleCloseModal}
                />
              </div>
            </div>
          </TooltipProvider>
        </SoundEffectProvider>
      </MusicProvider>
    </ErrorBoundary>
  );
}

export default App;
